import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  FaUser,
  FaCalendarAlt,
  FaEye,
  FaUsers,
  FaProjectDiagram,
  FaArrowLeft,
  FaEnvelope,
  FaShieldAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaCreditCard,
  FaIdBadge,
  FaBuilding,
  FaClock,
  FaFingerprint,
} from 'react-icons/fa';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface User {
  _id: string;
  name: string;
  email: string;
  paymentProvider?: string;
  role: string;
  firebaseUid?: string;
  emailVerified: boolean;
  photoURL?: string;
  providerId?: string;
  disabled: boolean;
  organization?: {
    name: string;
    teamMembers: any[];
  };
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

const truncateName = (name: string, max = 30) => {
  if (!name) return 'N/A';
  return name.length > max ? name.substring(0, max) + '...' : name;
};

const roleConfig: Record<string, { color: string; bg: string; label: string }> = {
  admin: { color: 'text-red-700', bg: 'bg-red-100', label: 'Admin' },
  agency_admin: { color: 'text-blue-700', bg: 'bg-blue-100', label: 'Agency Admin' },
  agency_member: { color: 'text-emerald-700', bg: 'bg-emerald-100', label: 'Agency Member' },
  member: { color: 'text-gray-700', bg: 'bg-gray-100', label: 'Member' },
};

const getRoleConfig = (role: string) => roleConfig[role] || roleConfig.member;

const RoleBadge: React.FC<{ role: string; size?: 'sm' | 'md' }> = ({ role, size = 'md' }) => {
  const cfg = getRoleConfig(role);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-semibold ${cfg.bg} ${cfg.color} ${size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-xs'}`}>
      <FaShieldAlt className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

const StatusBadge: React.FC<{ active: boolean; label?: [string, string] }> = ({
  active,
  label = ['Active', 'Disabled'],
}) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
    {active ? <FaCheckCircle className="w-3 h-3" /> : <FaTimesCircle className="w-3 h-3" />}
    {active ? label[0] : label[1]}
  </span>
);

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0">
    <div className="mt-0.5 w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 flex-shrink-0">
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
      <div className="text-sm font-medium text-gray-800">{children}</div>
    </div>
  </div>
);

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      try {
        const response = await api.get(`/admin/users/${userId}?include=projects`);
        return response.data.data || response.data;
      } catch {
        const fallback = await api.get(`/admin/users/${userId}`);
        return fallback.data.data || fallback.data;
      }
    },
    enabled: !!userId,
  });

  const user: User | null = data || null;
  const userProjects: any[] = Array.isArray(data?.projects) ? data.projects : [];

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name: string) => {
    if (!name) return 'NA';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <FaUser className="w-12 h-12 text-gray-300" />
        <p className="text-gray-500 font-medium">User not found</p>
        <button
          onClick={() => navigate('/users')}
          className="text-primary text-sm hover:underline"
        >
          Back to Users
        </button>
      </div>
    );
  }

  const teamMembers = user.organization?.teamMembers || [];

  return (
    <div className="space-y-6 pb-10">

      {/* Page Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/users')}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
        >
          <FaArrowLeft className="w-4 h-4" />
          Back to Users
        </button>
      </div>

      {/* Hero Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-primaryDark rounded-2xl shadow-xl"
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-white" />
          <div className="absolute -bottom-16 -left-10 w-72 h-72 rounded-full bg-white" />
        </div>

        <div className="relative p-6 md:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            {/* Avatar */}
            <div className="w-20 h-20 rounded-2xl bg-white bg-opacity-20 border-2 border-white border-opacity-30 flex items-center justify-center text-white text-2xl font-bold shadow-lg flex-shrink-0">
              {getInitials(user.name)}
            </div>

            {/* Name & Meta */}
            <div className="flex-1 min-w-0">
              <h1
                className="text-2xl md:text-3xl font-bold text-white leading-tight"
                title={user.name}
              >
                {truncateName(user.name, 30)}
              </h1>
              <p className="text-blue-100 text-sm mt-1 flex items-center gap-1.5">
                <FaEnvelope className="w-3.5 h-3.5 opacity-70" />
                {user.email}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="inline-flex items-center gap-1 bg-white bg-opacity-20 text-white text-xs font-semibold px-3 py-1 rounded-full border border-white border-opacity-20">
                  <FaShieldAlt className="w-3 h-3" />
                  {getRoleConfig(user.role).label}
                </span>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full ${!user.disabled ? 'bg-emerald-400 bg-opacity-20 text-emerald-100 border border-emerald-300 border-opacity-30' : 'bg-red-400 bg-opacity-20 text-red-100 border border-red-300 border-opacity-30'}`}>
                  {!user.disabled ? <FaCheckCircle className="w-3 h-3" /> : <FaTimesCircle className="w-3 h-3" />}
                  {!user.disabled ? 'Active' : 'Disabled'}
                </span>
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full ${user.emailVerified ? 'bg-blue-300 bg-opacity-20 text-blue-100 border border-blue-300 border-opacity-30' : 'bg-yellow-400 bg-opacity-20 text-yellow-100 border border-yellow-300 border-opacity-30'}`}>
                  {user.emailVerified ? <FaCheckCircle className="w-3 h-3" /> : <FaTimesCircle className="w-3 h-3" />}
                  {user.emailVerified ? 'Email Verified' : 'Email Not Verified'}
                </span>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex sm:flex-col gap-4 sm:gap-3 text-right">
              <div className="text-center sm:text-right">
                <p className="text-2xl font-bold text-white">{userProjects.length}</p>
                <p className="text-blue-200 text-xs">Projects</p>
              </div>
              <div className="text-center sm:text-right">
                <p className="text-2xl font-bold text-white">{teamMembers.length}</p>
                <p className="text-blue-200 text-xs">Team Members</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Basic Information */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary bg-opacity-10 flex items-center justify-center">
              <FaUser className="w-4 h-4 text-primary" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">Basic Information</h2>
          </div>

          <InfoRow icon={<FaUser className="w-4 h-4" />} label="Full Name">
            <span title={user.name}>{truncateName(user.name, 30)}</span>
          </InfoRow>
          <InfoRow icon={<FaEnvelope className="w-4 h-4" />} label="Email Address">
            <span className="break-all">{user.email}</span>
          </InfoRow>
          <InfoRow icon={<FaShieldAlt className="w-4 h-4" />} label="Role">
            <RoleBadge role={user.role} size="sm" />
          </InfoRow>
          <InfoRow icon={<FaCheckCircle className="w-4 h-4" />} label="Account Status">
            <StatusBadge active={!user.disabled} />
          </InfoRow>
          <InfoRow icon={<FaEnvelope className="w-4 h-4" />} label="Email Verified">
            <StatusBadge active={user.emailVerified} label={['Verified', 'Not Verified']} />
          </InfoRow>
          {user.providerId && (
            <InfoRow icon={<FaIdBadge className="w-4 h-4" />} label="Provider ID">
              <span className="font-mono text-xs bg-gray-50 px-1.5 py-0.5 rounded">{user.providerId}</span>
            </InfoRow>
          )}
          {user.paymentProvider && (
            <InfoRow icon={<FaCreditCard className="w-4 h-4" />} label="Payment Provider">
              <span className="capitalize">{user.paymentProvider}</span>
            </InfoRow>
          )}
        </motion.div>

        {/* Organization */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <FaBuilding className="w-4 h-4 text-blue-500" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">Organization</h2>
          </div>

          <InfoRow icon={<FaBuilding className="w-4 h-4" />} label="Organization Name">
            {user.organization?.name || <span className="text-gray-400 italic">No Organization</span>}
          </InfoRow>
          <InfoRow icon={<FaUsers className="w-4 h-4" />} label="Team Size">
            <span>{teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}</span>
          </InfoRow>

          {teamMembers.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Team</p>
              <div className="space-y-2">
                {teamMembers.slice(0, 4).map((member, i) => (
                  <button
                    key={member._id || i}
                    onClick={() => navigate(`/users/${member._id}`)}
                    className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-primaryDark flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                      {getInitials(member.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-800 truncate" title={member.name}>
                        {truncateName(member.name, 20)}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{member.email}</p>
                    </div>
                    <FaEye className="w-3 h-3 text-gray-300 group-hover:text-primary transition-colors flex-shrink-0" />
                  </button>
                ))}
                {teamMembers.length > 4 && (
                  <p className="text-xs text-gray-400 text-center py-1">+{teamMembers.length - 4} more members</p>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* Account Information */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
              <FaCalendarAlt className="w-4 h-4 text-purple-500" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">Account Info</h2>
          </div>

          <InfoRow icon={<FaCalendarAlt className="w-4 h-4" />} label="Member Since">
            {formatDate(user.createdAt)}
          </InfoRow>
          <InfoRow icon={<FaClock className="w-4 h-4" />} label="Last Updated">
            {formatDate(user.updatedAt)}
          </InfoRow>
          <InfoRow icon={<FaFingerprint className="w-4 h-4" />} label="User ID">
            <span className="font-mono text-xs bg-gray-50 px-1.5 py-0.5 rounded break-all">{user._id}</span>
          </InfoRow>
          {user.firebaseUid && (
            <InfoRow icon={<FaIdBadge className="w-4 h-4" />} label="Firebase UID">
              <span className="font-mono text-xs bg-gray-50 px-1.5 py-0.5 rounded break-all">{user.firebaseUid}</span>
            </InfoRow>
          )}
        </motion.div>
      </div>

      {/* Projects Section */}
      {userProjects.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                <FaProjectDiagram className="w-4 h-4 text-orange-500" />
              </div>
              <h2 className="text-base font-semibold text-gray-900">Projects</h2>
            </div>
            <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
              {userProjects.length} total
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {userProjects.map((project, index) => {
              const statusMap: Record<string, string> = {
                active: 'bg-emerald-100 text-emerald-700',
                completed: 'bg-blue-100 text-blue-700',
                pending: 'bg-yellow-100 text-yellow-700',
                published: 'bg-purple-100 text-purple-700',
              };
              const statusClass = statusMap[project.status] || 'bg-gray-100 text-gray-600';
              const projectName = project.projectName || project.name || 'Untitled Project';

              return (
                <button
                  key={project._id || index}
                  onClick={() => navigate(`/projects/${project._id}`, { state: { from: 'users' } })}
                  className="group text-left border border-gray-100 rounded-xl p-4 hover:border-primary hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4
                      className="font-semibold text-gray-900 text-sm leading-tight"
                      title={projectName}
                    >
                      {truncateName(projectName, 30)}
                    </h4>
                    <FaEye className="w-4 h-4 text-gray-300 group-hover:text-primary transition-colors flex-shrink-0 mt-0.5" />
                  </div>
                  {project.description && (
                    <p className="text-xs text-gray-400 line-clamp-2 mb-3">
                      {project.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${statusClass}`}>
                      {project.status || 'draft'}
                    </span>
                    <span className="text-xs text-gray-400">{formatDate(project.createdAt)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Team Members Full List */}
      {teamMembers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
                <FaUsers className="w-4 h-4 text-teal-500" />
              </div>
              <h2 className="text-base font-semibold text-gray-900">Team Members</h2>
            </div>
            <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
              {teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Member</th>
                  <th className="pb-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Role</th>
                  <th className="pb-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="pb-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Joined</th>
                  <th className="pb-3 w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {teamMembers.map((member, index) => (
                  <tr
                    key={member._id || index}
                    className="group hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/users/${member._id}`)}
                  >
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primaryDark flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {getInitials(member.name)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate" title={member.name}>
                            {truncateName(member.name, 30)}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <RoleBadge role={member.role} size="sm" />
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-col gap-1">
                        <StatusBadge active={!member.disabled} />
                        <StatusBadge active={member.emailVerified} label={['Email Verified', 'Not Verified']} />
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="text-xs text-gray-400">{formatDate(member.createdAt)}</span>
                    </td>
                    <td className="py-3">
                      <FaEye className="w-4 h-4 text-gray-300 group-hover:text-primary transition-colors" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default UserDetail;
